/**
 * Statement Upload Starter Modal
 * ------------------------------
 * Every "Upload statement" CTA opens this first (guide + limits +
 * connected-account warning + template downloads). Its primary CTA
 * then invokes `onSelectFile()` → the hosting page's existing hidden
 * <input type="file"> fires the OS picker and hands off to the
 * unchanged handleFileSelect + downstream bank-account picker + LLM
 * upload flow.
 *
 * When `bankAccountId` is provided, we run an upload precheck to
 * detect Mono/Plaid-connected accounts. If connected, an amber
 * warning banner + explicit "I understand" checkbox gates the CTA
 * so the user cannot silently double-book.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Upload, ShieldCheck, FileDown, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_ROWS = 50000;

export const StatementUploadDialog = ({
  open, onOpenChange, onSelectFile, bankAccountId = null,
}) => {
  const [precheck, setPrecheck] = useState(null);
  const [loadingPrecheck, setLoadingPrecheck] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset acknowledgement whenever the modal (re)opens on a different
  // account so a previous "yes" doesn't carry over.
  useEffect(() => {
    if (!open) return;
    setAcknowledged(false);
    setPrecheck(null);
    if (!bankAccountId) return;
    setLoadingPrecheck(true);
    apiClient
      .get(`/api/bank-feeds/upload-precheck?bank_account_id=${bankAccountId}`)
      .then(({ data }) => setPrecheck(data))
      .catch(() => setPrecheck(null))
      .finally(() => setLoadingPrecheck(false));
  }, [open, bankAccountId]);

  const downloadTemplate = (kind) => {
    const url = `${API}/api/bank-feeds/${kind}-template`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`${kind.toUpperCase()} template downloading…`);
  };

  const handlePickFile = () => {
    onOpenChange(false);
    setTimeout(() => {
      try { onSelectFile?.(); } catch (_) { /* noop */ }
    }, 50);
  };

  const isConnected = !!precheck?.connected;
  const gated = isConnected && !acknowledged;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="statement-upload-starter"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#108981]" />
            Upload your bank statement
          </DialogTitle>
          <DialogDescription>
            Upload your <strong>actual bank statement</strong> — exactly as your bank
            exports it. Our AI parser reads every format automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Loading state for precheck */}
          {loadingPrecheck && (
            <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Checking account connection status…
            </div>
          )}

          {/* Connected-account warning */}
          {isConnected && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
              data-testid="connected-account-warn"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 space-y-1.5">
                  <p className="font-semibold text-[13px]">
                    This account is connected via {precheck.provider === 'plaid' ? 'Plaid' : 'Mono'}.
                  </p>
                  <p>
                    Uploading a statement here can create <strong>duplicate transactions</strong>.
                    You only need to upload manually if your bank feed has a gap
                    that {precheck.provider === 'plaid' ? 'Plaid' : 'Mono'} did not fill.
                  </p>
                  {precheck?.coverage_last_close_date && (
                    <p className="text-amber-800">
                      Your books currently cover up to{' '}
                      <strong>{precheck.coverage_last_close_date}</strong>
                      {precheck?.coverage_last_close_balance != null && (
                        <> at <strong>{precheck.coverage_last_close_balance.toLocaleString()}</strong></>
                      )}
                      .
                    </p>
                  )}
                </div>
              </div>
              <label
                className="flex items-center gap-2 pl-6 cursor-pointer select-none"
                data-testid="connected-account-ack-label"
              >
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(!!v)}
                  data-testid="connected-account-continue-anyway"
                />
                <span className="text-xs text-amber-900">
                  I understand and want to continue anyway.
                </span>
              </label>
            </div>
          )}

          {/* Guide + limits */}
          <div
            className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-1.5"
            data-testid="upload-guide-panel"
          >
            <div className="flex items-center gap-1.5 font-semibold text-slate-900 text-[13px]">
              <ShieldCheck className="w-4 h-4 text-[#108981]" />
              Upload guide &amp; limits
            </div>
            <ul className="list-disc list-inside space-y-0.5 pl-1">
              <li>Formats: <strong>PDF, XLS, XLSX, CSV, OFX/QFX, PNG/JPG scans</strong></li>
              <li>Max <strong>{MAX_ROWS.toLocaleString()}</strong> transactions per file</li>
              <li data-testid="upload-guide-multi"><strong>Several months at once:</strong> select up to 24 statements for the same account — they are read in parallel, joined with balance checks (no gaps, no overlaps) and imported as one</li>
              <li>Statement must include an <strong>opening &amp; closing balance</strong>, or a <strong>running-balance</strong> column</li>
              <li>Currency must match the selected bank account</li>
              <li>Uploads run in the background — you can close this window safely</li>
            </ul>
          </div>

          {/* Advanced options — intentionally muted so users don't
              gravitate to it. This is the rare-case fallback for when a
              bank export is genuinely unusable; the primary path is
              still "Select statement file" above with the guide. */}
          <Accordion type="single" collapsible className="border border-slate-100 rounded-lg bg-slate-50/40">
            <AccordionItem value="advanced" className="border-none">
              <AccordionTrigger
                className="px-3 py-1.5 text-[11px] font-normal text-slate-400 hover:text-slate-500 hover:no-underline [&>svg]:text-slate-300 [&>svg]:w-3.5 [&>svg]:h-3.5"
                data-testid="upload-advanced-trigger"
              >
                Advanced options
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 text-xs text-slate-500 space-y-2">
                <p>
                  Only use this if your bank&apos;s export is genuinely unusable
                  (missing columns, scrambled data, unreadable format). For
                  everyone else, uploading the bank&apos;s own statement gives
                  the best accuracy.
                </p>
                <p>
                  Download our template, paste your transactions into it, then
                  upload it back through the button above. It&apos;s pre-formatted
                  to always pass our parser.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-7 px-2 text-[11px] font-normal text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                    onClick={() => downloadTemplate('csv')}
                    data-testid="download-csv-template-btn"
                  >
                    <FileDown className="w-3 h-3 mr-1" />
                    CSV template
                  </Button>
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-7 px-2 text-[11px] font-normal text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                    onClick={() => downloadTemplate('xlsx')}
                    data-testid="download-xlsx-template-btn"
                  >
                    <FileDown className="w-3 h-3 mr-1" />
                    Excel template
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="upload-starter-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePickFile}
            disabled={gated}
            className="bg-[#108981] hover:bg-[#0a6b65]"
            data-testid="upload-starter-select-file-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Select statement file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatementUploadDialog;
